import { useToast } from "@/components/ui/use-toast";
import {
  getDateRangeSyncUrl,
  getMagicLinkUrl,
  getProviderSyncUrl,
  getCalcUrl,
  commissionAuthorization,
} from "@/lib/api";
import { useAppStore } from "@/stores";
import { DateRange } from "react-day-picker";
import { mutate as globalMutate } from "swr";
import { useCommissionAccess } from "./useCommissionAccess";

export const useAPI = () => {
  const { data: commissionAccess, error: accessError, isLoading: accessLoading } = useCommissionAccess();
  const canCalculateCommissions = !!commissionAccess?.user_ids.length && !accessError && !accessLoading;
  const { currentUser, isCalculating, isSyncing, setCalculating, setSyncing } =
    useAppStore((state) => state);

  // For backward compatibility
  const isLoading = isSyncing || isCalculating;
  const { toast } = useToast();

  const handleAPIError = async (response: Response) => {
    const { status } = response || {};

    // An error occurred
    const errorText = await response.text();
    if (status === 401) {
      // Temporarily disable this error due to toast spam for younium
      // toast({
      //   title: errorText || "Failed to renew token",
      //   variant: "destructive",
      //   description: "You may need to reauthenticate the provider.",
      //   duration: 20000,
      // });
    } else {
      toast({
        title: "Could not sync data",
        variant: "destructive",
        description: errorText,
        // action: (
        //   <ToastAction onClick={handleClick} altText="Try again">
        //     Try again
        //   </ToastAction>
        // ),
        duration: 20000,
      });
    }

    return response;
  };

  const getError = (chunks: string[]) => {
    return chunks.find((c) => c.toLowerCase().includes("error"));
  };

  const hasError = (chunks: string[]) => {
    return chunks.some((c) => c.toLowerCase().includes("error"));
  };

  const tryFetchCatch = async (url: string) => {
    // try {
    setSyncing(true);
    return fetch(url, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${import.meta.env.VITE_API_KEY}`,
      },
    })
      .then((response) => {
        // Get the readable stream from the response body
        const stream = response.body;
        toast({
          title: `Synchronization initiated`,
          description: `Syncing data. This may take up to a minute.`,
          duration: 60000,
        });
        // Get the reader from the stream
        const reader = stream?.getReader();
        const chunks: string[] = [];

        // Define a function to read each chunk
        const readChunk = () => {
          // Read a chunk from the reader
          reader
            ?.read()
            .then(({ value, done }) => {
              // Check if the stream is done
              if (done) {
                // Log a message
                if (chunks.length > 0) {
                  if (hasError(chunks)) {
                    toast({
                      title: "Could not sync data",
                      variant: "destructive",
                      description: getError(chunks),
                      duration: 10000,
                    });
                  } else {
                    // Find the sync-complete event that contains the count
                    let syncCount = 0;
                    for (const chunk of chunks) {
                      if (chunk.includes("event: sync-complete")) {
                        try {
                          // Extract the data part from the SSE message
                          const dataMatch = chunk.match(/data: ({.*})/i);
                          if (dataMatch && dataMatch[1]) {
                            const eventData = JSON.parse(dataMatch[1]);
                            if (
                              eventData &&
                              typeof eventData.count === "number"
                            ) {
                              syncCount = eventData.count;
                              break;
                            }
                          }
                        } catch (e) {
                          console.error(
                            "Error parsing sync-complete event:",
                            e
                          );
                        }
                      }
                    }

                    toast({
                      title: `Synchronization completed`,
                      description: `${syncCount} deals were successfully synced.`,
                      duration: 10000,
                    });
                  }
                } else {
                  toast({
                    title: "Nothing to sync",
                    description:
                      "No deals found for the selected time period. Reconnect integrations if you believe this is an error.",
                    duration: 10000,
                  });
                }
                setSyncing(false);
                // Return from the function
                return;
              }
              // Convert the chunk value to a string

              const stringValue = new TextDecoder().decode(value, {
                stream: true,
              });
              // // Log the chunk string
              // console.log("value", chunkString);
              chunks.push(stringValue);

              // const json = JSON.parse(chunkString);
              // Read the next chunk
              readChunk();
            })
            .catch((error) => {
              // Log the error
              console.error(error);
            });
        };
        // Start reading the first chunk
        readChunk();
        return new Response();
      })
      .catch((error) => {
        // Log the error
        console.error(error);
        return new Response();
      });
    //   const reader = response?.body?.getReader();

    //   return new ReadableStream({
    //     start(controller) {
    //       return pump();
    //       function pump() {
    //         return reader.read().then(({ done, value }) => {
    //           // When no more data needs to be consumed, close the stream
    //           if (done) {
    //             controller.close();
    //             return;
    //           }
    //           // Enqueue the next data chunk into our target stream
    //           controller.enqueue(value);
    //           return pump();
    //         });
    //       }
    //     },
    //   });
    //   while (true) {
    //     const { value, done } = (await reader?.read()) || {};

    //     if (done) {
    //       console.log("The stream was already closed!");
    //       setIsLoading(false);
    //       break;
    //     } else {
    //       console.log(value);
    //     }
    //   }
    //   // setIsLoading(false);
    //   // if (!response.ok) return handleAPIError(response);
    //   return handleAPISuccess(response, scheduled);
    // } catch (error) {
    //   setIsLoading(false);
    //   return handleAPIError(
    //     new Response("An error occurred while fetching data.")
    //   );
    // }
  };

  const syncDealsByDateRange = async (
    dateRange: DateRange
  ): Promise<Response> => {
    if (!currentUser) return handleAPIError({} as Response);

    // Return a promise that resolves when sync is fully completed
    return new Promise((resolve) => {
      setSyncing(true);

      fetch(getDateRangeSyncUrl(currentUser.company_id, dateRange), {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${import.meta.env.VITE_API_KEY}`,
        },
      })
        .then((response) => {
          // Get the readable stream from the response body
          const stream = response.body;
          toast({
            title: `Synchronization initiated`,
            description: `Syncing data. This may take up to a minute.`,
            duration: 60000,
          });

          // Get the reader from the stream
          const reader = stream?.getReader();
          const chunks: string[] = [];

          // Define a function to read each chunk
          const readChunk = () => {
            // Read a chunk from the reader
            reader
              ?.read()
              .then(({ value, done }) => {
                // Check if the stream is done
                if (done) {
                  // Log a message
                  if (chunks.length > 0) {
                    if (hasError(chunks)) {
                      toast({
                        title: "Could not sync data",
                        variant: "destructive",
                        description: getError(chunks),
                        duration: 10000,
                      });
                    } else {
                      // Find the sync-complete event that contains the count
                      let syncCount = 0;
                      for (const chunk of chunks) {
                        if (chunk.includes("event: sync-complete")) {
                          try {
                            // Extract the data part from the SSE message
                            const dataMatch = chunk.match(/data: ({.*})/i);
                            if (dataMatch && dataMatch[1]) {
                              const eventData = JSON.parse(dataMatch[1]);
                              if (
                                eventData &&
                                typeof eventData.count === "number"
                              ) {
                                syncCount = eventData.count;
                                break;
                              }
                            }
                          } catch (e) {
                            console.error(
                              "Error parsing sync-complete event:",
                              e
                            );
                          }
                        }
                      }

                      toast({
                        title: `Synchronization completed`,
                        description: `${syncCount} deals were successfully synced.`,
                        duration: 10000,
                      });
                    }
                  } else {
                    toast({
                      title: "Nothing to sync",
                      description:
                        "No deals found for the selected time period. Reconnect integrations if you believe this is an error.",
                      duration: 10000,
                    });
                  }
                  setSyncing(false);

                  // Resolve the promise when sync is complete
                  resolve(
                    new Response(JSON.stringify({ success: true }), {
                      status: 200,
                      headers: { "Content-Type": "application/json" },
                    })
                  );
                  return;
                }

                // Convert the chunk value to a string
                const stringValue = new TextDecoder().decode(value, {
                  stream: true,
                });
                chunks.push(stringValue);

                // Read the next chunk
                readChunk();
              })
              .catch((error) => {
                // Log the error
                console.error(error);
                setSyncing(false);

                // Resolve with error
                resolve(
                  new Response(
                    JSON.stringify({ success: false, error: error.message }),
                    {
                      status: 500,
                      headers: { "Content-Type": "application/json" },
                    }
                  )
                );
              });
          };

          // Start reading the first chunk
          readChunk();
        })
        .catch((error) => {
          // Log the error
          console.error(error);
          setSyncing(false);

          // Resolve with error
          resolve(
            new Response(
              JSON.stringify({ success: false, error: error.message }),
              {
                status: 500,
                headers: { "Content-Type": "application/json" },
              }
            )
          );
        });
    });
  };

  const syncDealsByProvider = async (
    provider: string,
    providerId?: string
  ): Promise<Response> => {
    if (!currentUser) return handleAPIError({} as Response);
    return tryFetchCatch(
      getProviderSyncUrl(provider, currentUser.company_id, providerId)
    );
  };

  const getMagicLink = async (email: string) => {
    // Create a fetch query to sign in with Magic Link
    const response = await fetch(getMagicLinkUrl(email), {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: await commissionAuthorization(),
      },
    });
    if (!response.ok) {
      return handleAPIError(response);
    }
    return await response.json();
  };

  const calculateCommissions = async (
    dateRange: DateRange,
    scope?: { type: string; value: string } | null,
    isProjected: boolean = false,
    showToasts: boolean = true
  ): Promise<Response> => {
    // The page owns access errors. Do not start a write or show a second error toast.
    if (!canCalculateCommissions) {
      setCalculating(false);
      return new Response(null, { status: 503 });
    }
    if (!currentUser) return handleAPIError({} as Response);

    // Determine which userIds to include based on the provided scope
    let userIds: string[] | undefined = undefined;

    if (scope && scope.type === "user") {
      // If scope is a specific user, use only that user's ID
      userIds = [scope.value];
    }
    // For company scope, team scope or no scope, leave userIds undefined to include all users

    const isEphemeral = false; // DO save to db

    // Get the calculation URL path with the isProjected parameter
    const urlPath = getCalcUrl(
      currentUser,
      dateRange,
      userIds,
      isProjected,
      isEphemeral,
      true
    );
    if (!urlPath) return handleAPIError({} as Response);

    // Construct the full URL - make sure we don't have double slashes
    const apiBaseUrl = import.meta.env.VITE_API_URL.endsWith("/")
      ? import.meta.env.VITE_API_URL.slice(0, -1)
      : import.meta.env.VITE_API_URL;
    const fullUrl = `${apiBaseUrl}/${urlPath}`;

    console.log("Calling calculation API:", fullUrl);
    setCalculating(true);

    try {
      // Make the API request
      const response = await fetch(fullUrl, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: await commissionAuthorization(),
        },
      });

      console.log("API response status:", response.status, response.statusText);

      if (!response.ok) {
        setCalculating(false);
        toast({
          title: "Commissions could not be updated",
          description: response.status === 401
            ? "A required connection was rejected. Contact your administrator."
            : "The calculation failed. Reload the page to try again.",
          variant: "destructive",
        });
        return response;
      }

      const result = await response.json() as {
        data?: unknown[];
        error?: string;
        errors?: Array<{ message: string }>;
        logs?: string[];
      };
      await globalMutate(key => Array.isArray(key) && key[0] === "commissions");
      const messages = [
        ...(result.error ? [result.error] : []),
        ...(result.errors?.map(error => error.message) ?? []),
        ...(result.logs?.filter(log => /error/i.test(log)) ?? []),
      ];
      if (messages.length) {
        toast({
          title: "Some commissions could not be updated",
          description: [...new Set(messages)].join("\n").slice(0, 500),
          variant: "destructive",
        });
        return new Response(null, { status: 502 });
      }
      if (showToasts) {
        toast({
          title: "Calculation completed",
          description: `Successfully processed ${result.data?.length ?? 0} users.`,
        });
      }
      return new Response();
    } catch {
      toast({
        title: "Commissions could not be updated",
        description: "The calculation did not finish. Reload the page to try again.",
        variant: "destructive",
      });
      return new Response(null, { status: 502 });
    } finally {
      setCalculating(false);
    }
  };

  return {
    syncDealsByDateRange,
    syncDealsByProvider,
    calculateCommissions,
    canCalculateCommissions,
    getMagicLink,
    isLoading,
    isCalculating,
    isSyncing,
    setCalculating,
  };
};
